import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDatabase } from '../context/DatabaseContext';
import { useToast } from '../context/ToastContext';
import { LoadingSpinner } from '../components/shared/LoadingSpinner';
import SchemaViewer from '../components/ui/SchemaViewer';
import SchemaAnalysisViewer from '../components/ui/SchemaAnalysisViewer';
import { analyzeSchema, getTablesInfo, getSchemaAnalysis, getSemanticAnalysis, getDataProfiling, updateTableSelection } from '../services/api';
import { Database, Search, Sparkles, Brain, BarChart3, Lightbulb } from 'lucide-react';
import { formatLocalDate, formatLocalTime } from '../utils/timezone';
import { WriteGuard } from '../components/auth/WriteGuard';
import { useAuth } from '../context/AuthContext';

export const SchemaAnalysis = () => {
    const { t } = useTranslation(['page-schema-analysis', 'common']);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { databases, selectedDatabase, updateDatabaseStatus } = useDatabase();
    const { showToast } = useToast();
    const { canWrite } = useAuth();
    const [searchQuery, setSearchQuery] = useState('');
    const [activeTab, setActiveTab] = useState('tables');
    const [loading, setLoading] = useState(false);
    const [analyzing, setAnalyzing] = useState(false);
    const [tablesData, setTablesData] = useState(null);
    const [analysisData, setAnalysisData] = useState(null);
    const [semanticData, setSemanticData] = useState(null);
    const [profilingData, setProfilingData] = useState(null);
    const [selectionLocked, setSelectionLocked] = useState(false);
    const [savingSelection, setSavingSelection] = useState(false);

    const dbId = searchParams.get('db') || selectedDatabase?.id;
    const currentDb = databases.find(db => db.id === parseInt(dbId));
    const currentDbStatus = currentDb?.status;
    
    // Track if data has been fetched for current database
    const fetchedDbIdRef = useRef(null);

    const fetchData = useCallback(async (currentDbId) => {
        if (!currentDbId) return;
        setLoading(true);
        try {
            // Always fetch tables info (works for all databases)
            const tablesRes = await getTablesInfo(currentDbId);
            setTablesData(tablesRes.data?.data?.schema_info);
            setSelectionLocked(Boolean(tablesRes.data?.data?.selection_locked));

            // Fetch analysis data (will return 404 if not analyzed yet, which is expected)
            const [analysisRes, semanticRes, profilingRes] = await Promise.all([
                getSchemaAnalysis(currentDbId).catch(() => ({ data: {} })),
                getSemanticAnalysis(currentDbId).catch(() => ({ data: {} })),
                getDataProfiling(currentDbId).catch(() => ({ data: {} }))
            ]);

            setAnalysisData(analysisRes.data?.data);
            setSemanticData(semanticRes.data?.data?.semantic_analysis);
            setProfilingData(profilingRes.data?.data?.dataprofiling);
            fetchedDbIdRef.current = currentDbId;
        } catch (error) {
            console.error('Failed to fetch data:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch data when dbId changes
    useEffect(() => {
        if (!dbId) return;

        // Always fetch when dbId changes (don't use ref check)
        fetchData(dbId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dbId]);

    const handleAnalyze = async () => {
        if (!dbId) return;

        setAnalyzing(true);
        try {
            await analyzeSchema(dbId, { generate_queries: false, expose_endpoints: false });
            showToast(t('page-schema-analysis:toastAnalysisSuccess'), 'success');
            
            // Refresh database status in context FIRST
            await updateDatabaseStatus(dbId);
            
            // Then fetch the analysis data (now it should exist)
            await fetchData(dbId);
        } catch (error) {
            showToast(error.response?.data?.detail || t('page-schema-analysis:toastAnalysisFailed'), 'error');
        } finally {
            setAnalyzing(false);
        }
    };

    // Auto-saves the table selection on each toggle. Rethrows so SchemaViewer can
    // revert its optimistic state if the backend rejects the change.
    const handleSaveTableSelection = async (payload) => {
        if (!dbId) return;
        setSavingSelection(true);
        try {
            await updateTableSelection(dbId, { tables: payload });
        } catch (error) {
            showToast(error.response?.data?.detail || t('page-schema-analysis:toastSaveSelectionFailed'), 'error');
            throw error;
        } finally {
            setSavingSelection(false);
        }
    };

    const handleDatabaseSelect = (db) => {
        navigate(`/schema-analysis?db=${db.id}`);
    };

    // Check if ALL analysis data exists (schema analysis, semantic analysis, AND data profiling)
    // Button is disabled only when all three are present
    const hasAllAnalysis = analysisData?.analysis && semanticData?.length > 0 && profilingData?.length > 0;
    
    // Also check the database status from context as a fallback
    const isAnalyzed = hasAllAnalysis || currentDbStatus === 'analyzed';

    const filteredDatabases = databases.filter(db =>
        db.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const tabs = [
        { id: 'tables', label: t('page-schema-analysis:tabs.tables') },
        { id: 'analysis', label: t('page-schema-analysis:tabs.analysis') },
        { id: 'profiling', label: t('page-schema-analysis:tabs.profiling') },
        { id: 'semantic', label: t('page-schema-analysis:tabs.semantic') },
    ];

    return (
        <div className="flex gap-theme">
            {/* Left Sidebar - Database List */}
            <div className="w-64 bg-white border-r border-gray-200 flex flex-col rounded-xl shadow-sm h-fit max-h-[calc(100vh-8rem)]">
                {/* Sidebar Header */}
                <div className="p-6 border-b border-gray-200">
                    <h2 className="text-lg font-bold text-gray-900 mb-1">{t('page-schema-analysis:selectDatabase')}</h2>
                </div>

                {/* Search */}
                <div className="p-4 border-b border-gray-200">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder={t('page-schema-analysis:searchDatabasesPlaceholder')}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                    </div>
                </div>

                {/* Database List */}
                <div className="flex-1 overflow-y-auto p-4 space-y-2">
                        {filteredDatabases.length === 0 ? (
                            <p className="text-sm text-gray-500 text-center py-8">
                                {searchQuery ? t('page-schema-analysis:noMatchingDatabases') : t('page-schema-analysis:noDatabasesConnected')}
                            </p>
                        ) : (
                            filteredDatabases.map((db) => (
                                <div
                                    key={db.id}
                                    onClick={() => handleDatabaseSelect(db)}
                                    className={`p-3 rounded-lg border transition-all cursor-pointer group ${currentDb?.id === db.id
                                            ? 'bg-primary-50 border-primary-300'
                                            : 'bg-white border-gray-200 hover:border-primary-200 hover:bg-gray-50'
                                        }`}
                                >
                                    <div className="flex items-center gap-2">
                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${currentDb?.id === db.id ? 'bg-primary-100' : 'bg-green-100'
                                            }`}>
                                            <Database className={`w-4 h-4 ${currentDb?.id === db.id ? 'text-primary-600' : 'text-green-600'
                                                }`} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="font-semibold text-gray-900 text-sm truncate">
                                                    {db.name}
                                                </span>
                                                <span className={`px-2 py-0.5 text-xs font-medium rounded ${currentDb?.id === db.id
                                                        ? 'bg-green-100 text-green-700'
                                                        : 'bg-gray-100 text-gray-700'
                                                    }`}>
                                                    {db.status || t('page-schema-analysis:analyzedStatusFallback')}
                                                </span>
                                            </div>
                                            <p className="text-xs text-gray-500 mt-0.5">{db.db_type || t('page-schema-analysis:postgresqlFallback')}</p>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Right Panel - Analysis Content */}
                <div className="flex-1 page-content">
                    {/* Header */}
                        <div className="mb-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h1 className="page-title mb-1">{t('page-schema-analysis:title')}</h1>
                                    {currentDb && (
                                        <p className="text-sm text-gray-500">
                                            {currentDb.name} • {t('page-schema-analysis:databaseTypeLabel', { type: currentDb.db_type || t('page-schema-analysis:postgresqlFallback') })} • {t('page-schema-analysis:lastAnalyzedLabel', { date: formatLocalDate(currentDb.updated_at || currentDb.created_at) })}
                                        </p>
                                    )}
                                </div>
                                <WriteGuard>
                                    <button
                                        onClick={handleAnalyze}
                                        disabled={analyzing || !dbId || isAnalyzed}
                                        className={`px-4 py-2 ${
                                            isAnalyzed ? 'bg-gray-400' : 'bg-primary-600 hover:bg-primary-700'
                                        } text-white rounded-lg transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2`}
                                    >
                                        <Sparkles className="w-4 h-4" />
                                        {analyzing ? t('page-schema-analysis:analyzing') : isAnalyzed ? t('page-schema-analysis:analyzed') : t('page-schema-analysis:analyzeSchema')}
                                    </button>
                                </WriteGuard>
                            </div>

                            {/* Tabs */}
                            <div className="border-b border-gray-200">
                                <nav className="flex space-x-8">
                                    {tabs.map((tab) => (
                                        <button
                                            key={tab.id}
                                            onClick={() => setActiveTab(tab.id)}
                                            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === tab.id
                                                    ? 'border-primary-600 text-primary-600'
                                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                                }`}
                                        >
                                            {tab.label}
                                        </button>
                                    ))}
                                </nav>
                            </div>
                        </div>

                        {/* Tab Content */}
                        {!dbId ? (
                            <div className="bg-white rounded-lg border border-gray-200 text-center p-8">
                                <Database className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('page-schema-analysis:noDatabaseSelectedTitle')}</h3>
                                <p className="text-gray-600 mb-4">{t('page-schema-analysis:noDatabaseSelectedBody')}</p>
                            </div>
                        ) : loading ? (
                            <LoadingSpinner />
                        ) : (
                            <div>
                                {activeTab === 'tables' && (
                                    <div className="space-y-4">
                                        <SchemaViewer
                                            schemaData={tablesData}
                                            canManage={canWrite}
                                            locked={selectionLocked}
                                            saving={savingSelection}
                                            onSave={handleSaveTableSelection}
                                        />
                                    </div>
                                )}

                                {activeTab === 'analysis' && (
                                    <div className="space-y-4">
                                        {analysisData ? (
                                            <SchemaAnalysisViewer analysisData={analysisData} />
                                        ) : (
                                            <div className="bg-white rounded-lg border border-gray-200 text-center p-8">
                                                <Brain className="w-12 h-12 text-primary-400 mx-auto mb-3" />
                                                <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('page-schema-analysis:noSchemaAnalysisTitle')}</h3>
                                                <p className="text-gray-600 mb-4">{t('page-schema-analysis:noSchemaAnalysisBody')}</p>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {activeTab === 'profiling' && (
                                    <div className="space-y-6">
                                        {profilingData && Array.isArray(profilingData) ? (
                                            profilingData.map((profile, idx) => (
                                                <div key={idx} className="bg-white rounded-lg border border-gray-200 p-6">
                                                    <div className="flex items-center justify-between mb-4">
                                                        <h3 className="font-semibold text-gray-900 text-lg">{profile.table_name}</h3>
                                                        <div className="flex items-center gap-2">
                                                            {profile.profile_data?.table_statistics?.profiling_mode &&
                                                                profile.profile_data.table_statistics.profiling_mode !== 'exact' && (
                                                                <span className="px-2 py-1 text-xs font-medium bg-amber-100 text-amber-800 rounded">
                                                                    {t('page-schema-analysis:sampledStats')}
                                                                </span>
                                                            )}
                                                            <span className="px-3 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded">
                                                                {profile.profile_data?.table_statistics?.row_count_is_approximate ? '~' : ''}
                                                                {t('page-schema-analysis:rowsCount', { count: profile.row_count || 0 })}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                                                        <div className="bg-gray-50 rounded-lg p-4">
                                                            <p className="text-xs text-gray-600 mb-1">{t('page-schema-analysis:statusLabel')}</p>
                                                            <p className="text-sm font-medium text-gray-900">{profile.status || t('page-schema-analysis:unknownStatus')}</p>
                                                        </div>
                                                        <div className="bg-gray-50 rounded-lg p-4">
                                                            <p className="text-xs text-gray-600 mb-1">{t('page-schema-analysis:validationValuesLabel')}</p>
                                                            <div className="text-sm font-medium text-gray-900">
                                                                {profile.validation_values ? (
                                                                    Object.entries(profile.validation_values).map(([key, value]) => (
                                                                        <div key={key} className="truncate max-w-xs" title={`${key}: ${value}`}>
                                                                            <span className="font-semibold">{key}:</span> {String(value)}
                                                                        </div>
                                                                    ))
                                                                ) : t('page-schema-analysis:notAvailable')}
                                                            </div>
                                                        </div>
                                                        <div className="bg-gray-50 rounded-lg p-4">
                                                            <p className="text-xs text-gray-600 mb-1">{t('page-schema-analysis:profilingDateLabel')}</p>
                                                            <p className="text-sm font-medium text-gray-900">
                                                                {formatLocalDate(profile.profiled_at)}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    <div className="bg-gray-50 rounded-lg p-4">
                                                        <h4 className="text-sm font-semibold text-gray-900 mb-2">{t('page-schema-analysis:profileDataHeading')}</h4>
                                                        {profile.profile_data ? (
                                                            <div className="space-y-4">
                                                                {/* Table Statistics */}
                                                                {profile.profile_data.table_statistics && (
                                                                    <div>
                                                                        <h5 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">{t('page-schema-analysis:tableStatisticsHeading')}</h5>
                                                                        <div className="grid grid-cols-2 gap-2 text-xs">
                                                                            {Object.entries(profile.profile_data.table_statistics).map(([key, value]) => (
                                                                                <div key={key} className="flex justify-between bg-white p-2 rounded">
                                                                                    <span className="text-gray-600 capitalize">{key.replace('_', ' ')}:</span>
                                                                                    <span className="font-medium">{String(value)}</span>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                                
                                                                {/* Column Statistics */}
                                                                {profile.profile_data.column_statistics && (
                                                                    <div>
                                                                        <h5 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">{t('page-schema-analysis:columnStatisticsHeading')}</h5>
                                                                        <div className="space-y-2">
                                                                            {Object.entries(profile.profile_data.column_statistics).map(([colName, colStats]) => (
                                                                                <div key={colName} className="border border-gray-200 rounded p-3 bg-white">
                                                                                    <h6 className="font-medium text-sm text-gray-800 mb-2">{colName}</h6>
                                                                                    <div className="grid grid-cols-2 gap-2 text-xs">
                                                                                        {colStats.heavy_stats_skipped && (
                                                                                            <p className="text-xs text-amber-700 mb-1">
                                                                                                {t('page-schema-analysis:distinctValuesSkipped', { reason: colStats.skip_reason || t('page-schema-analysis:highCardinalityFallback') })}
                                                                                            </p>
                                                                                        )}
                                                                                        {Object.entries(colStats).map(([statKey, statValue]) => {
                                                                                            if (['heavy_stats_skipped', 'skip_reason', 'stats_approximate'].includes(statKey)) {
                                                                                                return null;
                                                                                            }
                                                                                            if (typeof statValue === 'object' && statValue !== null && !Array.isArray(statValue)) {
                                                                                                return null;
                                                                                            }
                                                                                            const approx = colStats.stats_approximate && ['distinct_count', 'null_percentage', 'null_count'].includes(statKey);
                                                                                            return (
                                                                                                <div key={statKey} className="flex justify-between bg-gray-50 p-1 rounded">
                                                                                                    <span className="text-gray-600 capitalize">{statKey.replace('_', ' ')}:</span>
                                                                                                    <span className="font-medium">
                                                                                                        {approx ? '~' : ''}{Array.isArray(statValue) ? statValue.join(', ') : String(statValue)}
                                                                                                    </span>
                                                                                                </div>
                                                                                            );
                                                                                        })}
                                                                                    </div>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <p className="text-xs text-gray-700">{t('page-schema-analysis:noProfileDataAvailable')}</p>
                                                        )}
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                                                <BarChart3 className="w-12 h-12 text-primary-400 mx-auto mb-3" />
                                                <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('page-schema-analysis:noDataProfilingTitle')}</h3>
                                                <p className="text-gray-600">{t('page-schema-analysis:noDataProfilingBody')}</p>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {activeTab === 'semantic' && (
                                    <div className="space-y-6">
                                        {semanticData && Array.isArray(semanticData) ? (
                                            semanticData.map((mapping, idx) => (
                                                <div key={idx} className="bg-white rounded-lg border border-gray-200 p-6">
                                                    <div className="flex items-start gap-3 mb-4">
                                                        <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                                            <Database className="w-5 h-5 text-primary-600" />
                                                        </div>
                                                        <div className="flex-1">
                                                            <h3 className="font-semibold text-gray-900 text-lg mb-1">
                                                                {mapping.domain_term || mapping.technical_name}
                                                            </h3>
                                                            <p className="text-sm text-gray-500">
                                                                {t('page-schema-analysis:entityMapping', { type: mapping.entity_type || t('page-schema-analysis:entityFallback') })}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                                        <div className="bg-gray-50 rounded-lg p-4">
                                                            <p className="text-xs text-gray-600 mb-1">{t('page-schema-analysis:technicalNameLabel')}</p>
                                                            <p className="text-sm font-medium text-gray-900">{mapping.technical_name || t('page-schema-analysis:notAvailable')}</p>
                                                        </div>
                                                        <div className="bg-gray-50 rounded-lg p-4">
                                                            <p className="text-xs text-gray-600 mb-1">{t('page-schema-analysis:domainTermLabel')}</p>
                                                            <p className="text-sm font-medium text-gray-900">{mapping.domain_term || t('page-schema-analysis:notAvailable')}</p>
                                                        </div>
                                                    </div>

                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                                        <div className="bg-gray-50 rounded-lg p-4">
                                                            <p className="text-xs text-gray-600 mb-1">{t('page-schema-analysis:isMetricLabel')}</p>
                                                            <p className="text-sm font-medium text-gray-900">{mapping.is_metric ? t('common:yes') : t('common:no')}</p>
                                                        </div>
                                                        <div className="bg-gray-50 rounded-lg p-4">
                                                            <p className="text-xs text-gray-600 mb-1">{t('page-schema-analysis:isDimensionLabel')}</p>
                                                            <p className="text-sm font-medium text-gray-900">{mapping.is_dimension ? t('common:yes') : t('common:no')}</p>
                                                        </div>
                                                    </div>

                                                    <div className="bg-gray-50 rounded-lg p-4">
                                                        <p className="text-xs text-gray-600 mb-1">{t('page-schema-analysis:createdAtLabel')}</p>
                                                        <p className="text-sm font-medium text-gray-900">
                                                            {formatLocalTime(mapping.created_at)}
                                                        </p>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                                                <Lightbulb className="w-12 h-12 text-amber-400 mx-auto mb-3" />
                                                <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('page-schema-analysis:noSemanticAnalysisTitle')}</h3>
                                                <p className="text-gray-600">{t('page-schema-analysis:noSemanticAnalysisBody')}</p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                </div>
            </div>
    );
};
